import axios from "axios";
import { User } from "../Entities/User";
import { EventDispatcher } from "../Utils/event-dispatcher";
import { NOTIFICATION_BANNER_CLOSED_KEY } from "../Utils/storage-constants";
import apiClient from "./api.client.service";
import organizationService from "./organization.service";

export default class AuthService extends EventDispatcher {
  public static EVENTS = {
    USER_SIGNED_IN: "USER_SIGNED_IN",
    USER_SIGNED_OUT: "USER_SIGNED_OUT",
    INITIAL_SETUP_DONE: "INITIAL_SETUP_DONE",
  };
  private APIBaseUrl: string;
  private static instance: AuthService;
  private currentUser: User | null = null;

  private constructor() {
    super();
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  init = () => {
    this.checkTokenAndRestoreCurrentUser();
  };

  async checkTokenAndRestoreCurrentUser() {
    const accessToken = this.getAccessTokenFromLocalStorage();
    if (accessToken) {
      await this.fetchAndStoreCurrentUser();
      return;
    } else {
      this.currentUser = null;
    }
  }

  async signIn(data: { email: string; password: string }) {
    try {
      const loginUrl = `${this.APIBaseUrl}/auth/login`;
      console.log("🔧 AuthService signIn - Attempting to connect to:", loginUrl);
      console.log("🔧 AuthService APIBaseUrl:", this.APIBaseUrl);
      const response = await apiClient.post(
        loginUrl,
        data
      );
      if (response.data && response.data.access_token) {
        console.log(
          "🚀 ~ AuthService ~ signIn ~ response.data.access_token:",
          response.data.access_token
        );
        this.setAccessTokenToLocalStorage(response.data.access_token);
        await this.fetchAndStoreCurrentUser();
      }
      return response;
    } catch (error) {
      console.error("🚀 ~ AuthService ~ signIn ~ error:", error);
      if (axios.isAxiosError(error)) {
        return error.response;
      }
    }
  }

  async orgSignUp(data: any, screeningSessionId?: string) {
    try {
      // Phase 7: Append screening_session_id as query param if provided
      let url = `${this.APIBaseUrl}/auth/org/signup`;
      if (screeningSessionId) {
        url += `?screening_session_id=${encodeURIComponent(screeningSessionId)}`;
      }

      const response = await apiClient.post(url, data);
      console.debug("response", response);
      return response;
    } catch (error) {
      console.error("🚀 ~ AuthService ~ orgSignUp ~ error:", error);
      if (axios.isAxiosError(error)) {
        return error.response;
      }
    }
  }

  async signOut() {
    await this.clearAccessTokenFromLocalStorage();
    this.setCurrentUser(null);
    organizationService.clearOrganizationData();
  }

  async fetchAndStoreCurrentUser() {
    if (this.currentUser) return this.currentUser;
    try {
      const response = await apiClient.get(`${this.APIBaseUrl}/users/me`, {
        headers: { 'X-Auth-Check': 'true' }
      });
      if (response && response.data) {
        this.setCurrentUser(response.data);
      }
      return "Succcess";
    } catch (error) {
      console.error("AuthService: fetchAndStoreCurrentUser: error", error);
      if (axios.isAxiosError(error)) {
        console.error(
          "AuthService: fetchAndStoreCurrentUser: axios error",
          error
        );
      } else {
        console.error(
          "AuthService: fetchAndStoreCurrentUser: unknown error",
          error
        );
      }
      return "fail";
    }
  }

  getCurrentUser(): User | null {
    if (!this.currentUser) {
      this.checkTokenAndRestoreCurrentUser();
    }
    return this.currentUser;
  }

  setCurrentUser(user: any) {
    this.currentUser = user;
    if (user) {
      this.dispatch(AuthService.EVENTS.USER_SIGNED_IN, {
        currentUser: user,
      });
    } else {
      this.dispatch(AuthService.EVENTS.USER_SIGNED_OUT);
    }
  }

  setAccessTokenToLocalStorage(token: string) {
    localStorage.setItem("access-token", token);
  }

  getAccessTokenFromLocalStorage(): string | null {
    return localStorage.getItem("access-token");
  }

  clearAccessTokenFromLocalStorage() {
    localStorage.removeItem("access-token");
    localStorage.removeItem("customTheme");
    localStorage.removeItem(NOTIFICATION_BANNER_CLOSED_KEY);
    // Clear screening session state so a new sign-in always starts fresh
    sessionStorage.removeItem("pending_screening_session_id");
    sessionStorage.removeItem("screening_case_type");
    sessionStorage.removeItem("screening_cta_type");
    sessionStorage.removeItem("screening_job_description");
    sessionStorage.removeItem("screening_resume_file_name");
  }

  async sendPasswordResetMail(email: string) {
    try {
      const formData = new FormData();
      formData.append("email", email);

      const response = await axios.post(
        `${this.APIBaseUrl}/admin/users/forgot-password`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log("🚀 ~ AuthService ~ sendPasswordResetMail ~ error:", error);
    }
  }

  async resetPassword(token: string, password: string) {
    try {
      const formData = new FormData();
      formData.append("token", token);
      formData.append("password", password);

      const response = await axios.post(
        `${this.APIBaseUrl}/admin/users/reset-password`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log("🚀 ~ AuthService ~ resetPassword ~ error:", error);
    }
  }

  async sendToken(token: string) {
    const token_id = {
      token: token,
    };
    await apiClient.post(
      `${this.APIBaseUrl}/calendar/auth/token-credentials`,
      token_id
    );
  }

  async unlinkCalendar() {
    const response = await apiClient.delete(`${this.APIBaseUrl}/calendar/auth`);
    return response;
  }

  handleInitialSetupDone() {
    this.dispatch(AuthService.EVENTS.INITIAL_SETUP_DONE);
  }

  async updateCurrentUser(user: User) {
    try {
      // Only send fields that are allowed by the backend UpdateUserDTO
      // Excludes sensitive fields like password, verificationToken, createdAt, updatedAt
      const updatePayload = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        isEmailVerified: user.isEmailVerified,
        orgId: user.orgId,
        isActive: user.isActive,
        role: user.role,
        isWalkthroughCompleted: user.isWalkthroughCompleted,
      };
      await apiClient.put(`${this.APIBaseUrl}/users/${user._id}`, updatePayload);
      this.setCurrentUser(user);
    } catch (error: any) {
      console.error("AuthService: updateCurrentUser: error", error);
    }
  }
}
